import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../api/client";
import type { components } from "../api/types";

type Node = components["schemas"]["Node"];
export type ReviewContext = components["schemas"]["ReviewContext"];

// Both queues fetched in parallel. Each runs as its own query, so a mutation
// refetches only the queues it moves: an approval moves the decisions queue,
// and an answer moves both (a saved changed answer changes which decisions
// block the Run button and which it will replace, 0157-15). The split limits
// only those mutation refetches: both queues still refetch when a run ends
// (useRunStream) and on window focus (main.tsx refetchOnWindowFocus).
//
// Return shape includes:
//   - `draftedNodeIds` — a Set of node IDs that have a
//     saved-but-not-yet-folded draft for this product. Consumers
//     subtract from queue length to compute the "open" / "pending"
//     counter, and use it to mark drafted nodes visually.
//   - `answersUnderReviewNodeIds` (questions queue only, P3) — node
//     IDs in `assumed_pending` status. The founder answered, the
//     system holds it as a working assumption pending downstream
//     confirmation. Surfaced as items so the founder can navigate
//     Previous/Next; subtracted from the counter so "answered =
//     closed" matches the founder's mental model. The server leaves
//     out a drafted id (it is in `draftedNodeIds`) and an id awaiting
//     the founder's covered-by ruling (it counts open), so drafted /
//     under review / neither are pairwise disjoint (0148).
//   - `triggerBlockingNodeIds` (0148 A21) — the served
//     `trigger_blocking_node_ids`: the ids of THIS queue's type the
//     server's trigger gate blocks a run on (middleware.
//     TriggerBlockingNodeIDs — undrafted, non-excluded `needs_user`
//     questions / `proposed` decisions, less a proposed decision a saved
//     changed answer will replace (0157-15, founder ruling Q5)). The
//     Product page's Run button reads this, never the display counters.
//   - `coveredByRulingNodeIds` (0148 A27; questions queue only, empty
//     on the decisions queue) — the served `covered_by_ruling_node_ids`:
//     questions awaiting the founder's covered-by ruling
//     (models.QuestionAwaitsCoveredByRuling over the same draft-overlaid
//     tree the items come from, drafted or not). The Review page offers
//     the "already covered by" row only for these ids.
//   - `replacedByNewAnswerNodeIds` (0157-15, founder ruling Q8; decisions
//     queue only, empty on the questions queue) - the served
//     `replaced_by_new_answer_node_ids`: proposed decisions a saved changed
//     answer will replace when the founder runs, less those the founder
//     confirmed or rejected in the same save; they never block the Run
//     button, and the Review card shows REPLACED_BY_NEW_ANSWER_HINT
//     (lib/exploratoryReading) on them.
//   - `context` (both queues; 0051 S1c) - the served `context`
//     (planner.ReviewContextOf, from the same read as the items): the tree's
//     copies of the nodes the items name (each covered-by holder with its
//     stored fields, the root) and the meta pieces the Review cards read. The
//     Review page reads it instead of /tree. EMPTY_REVIEW_CONTEXT when the
//     server serves none.
export type Queue = {
  items: Node[];
  draftedNodeIds: Set<string>;
  answersUnderReviewNodeIds: Set<string>;
  triggerBlockingNodeIds: Set<string>;
  coveredByRulingNodeIds: Set<string>;
  replacedByNewAnswerNodeIds: Set<string>;
  context: ReviewContext;
};

// The context a queue carries when the server serves none (a stub, or a
// service older than 0051 S1c): nothing named and no meta, so every card line
// that needs one stays hidden and never prints a raw id.
export const EMPTY_REVIEW_CONTEXT: ReviewContext = {
  nodes: [],
  meta: { topics: {}, ring_status: {}, completeness_targets: {} },
};

// 0051 S1c: the ONE client read of what waits on the founder in a served
// queue - every item the server holds no save for (not in drafted_node_ids)
// and no answer under review for (not in answers_under_review_node_ids, which
// leaves out a question awaiting the covered-by ruling, so that one waits).
// Review's tab counts and its forward moves ("done" only when nothing waits)
// read it; 0167's served classification replaces its body when it lands.
export function waitingNodeIds(q: Queue): Set<string> {
  return new Set(
    q.items.filter((n) => !q.draftedNodeIds.has(n.id) && !q.answersUnderReviewNodeIds.has(n.id)).map((n) => n.id),
  );
}

export function useQuestions(productId: string) {
  return useQuery({
    queryKey: ["product", productId, "queue", "questions"],
    queryFn: async (): Promise<Queue> => {
      const { data, error } = await apiClient.GET(
        "/api/v1/products/{pid}/queue/questions",
        { params: { path: { pid: productId } } },
      );
      if (error) throw error;
      return {
        items: data?.questions ?? [],
        draftedNodeIds: new Set(data?.drafted_node_ids ?? []),
        answersUnderReviewNodeIds: new Set(
          data?.answers_under_review_node_ids ?? [],
        ),
        triggerBlockingNodeIds: new Set(data?.trigger_blocking_node_ids ?? []),
        coveredByRulingNodeIds: new Set(data?.covered_by_ruling_node_ids ?? []),
        // A decision a new answer replaces is a decisions-queue list; empty
        // keeps the consumer shape symmetric.
        replacedByNewAnswerNodeIds: new Set<string>(),
        context: data?.context ?? EMPTY_REVIEW_CONTEXT,
      };
    },
  });
}

export function useDecisions(productId: string) {
  return useQuery({
    queryKey: ["product", productId, "queue", "decisions"],
    queryFn: async (): Promise<Queue> => {
      const { data, error } = await apiClient.GET(
        "/api/v1/products/{pid}/queue/decisions",
        { params: { path: { pid: productId } } },
      );
      if (error) throw error;
      return {
        items: data?.decisions ?? [],
        draftedNodeIds: new Set(data?.drafted_node_ids ?? []),
        // Decisions queue has no "under review" analogue today (no
        // `proposed → assumed_pending`-equivalent intermediate state).
        // Empty set keeps the consumer math symmetric.
        answersUnderReviewNodeIds: new Set<string>(),
        triggerBlockingNodeIds: new Set(data?.trigger_blocking_node_ids ?? []),
        // The covered-by ruling is a question act; empty keeps the
        // consumer shape symmetric.
        coveredByRulingNodeIds: new Set<string>(),
        replacedByNewAnswerNodeIds: new Set(data?.replaced_by_new_answer_node_ids ?? []),
        context: data?.context ?? EMPTY_REVIEW_CONTEXT,
      };
    },
  });
}

// ---- A refused or failed save, in founder language (0159) ------------------
//
// The draft post (POST /answers, /approvals) is checked against the tree as it
// stands and stores nothing when a choice no longer applies (0159 P2): 409
// draft.stale for a decision that is no longer proposed (or a re-reject whose
// new reason differs), 409 draft.covered_by_refused for a covered-by ruling the
// question no longer takes. toSaveError maps what the two submit hooks see by
// CODE (below), so the founder never reads the server's text for a code this
// file cannot vouch for. This file is the ONE home of the refusal and retry
// sentences: the Review page shows a DraftRefusedError in its refusal notice
// (delivered through the hooks' onRefused option as a DraftRefusal, kept in
// the notice's list by withRefusal and worded by
// refusalNoticeLine) and any other Error's message after its inline
// "Couldn't save: " prefix.

// The retry sub-line for a save that failed for no reason the founder can act
// on (a non-JSON or empty body, a network failure, api.internal, a code this
// file does not know), rendered after the inline prefix.
export const SAVE_RETRY_MESSAGE = "try again in a moment.";
// The two endings of a refusal: the choice was not saved, or (a re-reject of a
// rejected decision) the new reason was not.
const CHOICE_NOT_SAVED = ", so your choice wasn't saved.";
const REASON_NOT_SAVED = ", so your new reason wasn't saved.";

// The draft.stale statuses the refusal names as they are; any other status
// (the server's defensive "changed") reads as the decision having changed.
const NAMED_STALE_STATUSES: ReadonlySet<string> = new Set(["accepted", "rejected", "withdrawn"]);

function staleRefusalLine(status: unknown, reasonDiffers: unknown): string {
  if (typeof status === "string" && NAMED_STALE_STATUSES.has(status)) {
    return `This decision is already ${status}${reasonDiffers === true ? REASON_NOT_SAVED : CHOICE_NOT_SAVED}`;
  }
  return `This decision has already changed${CHOICE_NOT_SAVED}`;
}

// The server's code for a covered-by ruling the question no longer takes.
const COVERED_BY_REFUSED_CODE = "draft.covered_by_refused";

const COVERED_BY_REFUSED_LINE = `This isn't covered by that decision anymore${CHOICE_NOT_SAVED}`;

// The covered-by refusal naming both sides - the question and the decision the
// row played back as covering it (the Changes page's Q1 (a) form, the holder
// named) - for every covered-by refusal whose two titles are known, on screen
// or off (founder ruling 2026-09-26, option A): the refetch the refusal
// triggers usually hides the row or re-points it to a new holder, so "that
// decision" would point at nothing, or at the wrong decision.
function coveredByHolderRefusedLine(title: string, holderTitle: string): string {
  return `"${title}" isn't covered by "${holderTitle}" anymore${CHOICE_NOT_SAVED}`;
}

// DraftRefusedError is a save the server REFUSED because the item moved on
// (409 draft.stale or draft.covered_by_refused): nothing was stored, and its
// message is the founder's refusal line. `code` is the server's error code and
// `nodeId` the refused item's id (error.hints.node_id; "" when absent).
export class DraftRefusedError extends Error {
  readonly code: string;
  readonly nodeId: string;
  constructor(code: string, nodeId: string, message: string) {
    super(message);
    this.name = "DraftRefusedError";
    this.code = code;
    this.nodeId = nodeId;
  }
}

function asRecord(v: unknown): Record<string, unknown> | undefined {
  return typeof v === "object" && v !== null ? (v as Record<string, unknown>) : undefined;
}

// The draft POSTs' non-refusal codes whose message is a FIXED sentence the
// server wrote for the route (orchestrator postDrafts: 400 draft.invalid's
// sentences, 400 api.bad_request's "invalid body", 404 api.not_found's
// "product not found"). Only these pass the server's text on; api.internal's
// message is the Go error's own text, and a code this file does not know is
// text it cannot vouch for, so both read as the retry line.
const FIXED_SENTENCE_CODES: ReadonlySet<string> = new Set(["draft.invalid", "api.bad_request", "api.not_found"]);

// toSaveError maps what a draft POST failed with to what the submit hooks
// throw, by CODE:
//   - an error envelope ({error:{code,message,hints}}) coded draft.stale is a
//     DraftRefusedError whose line is built from hints.status and
//     hints.reason_differs; draft.covered_by_refused is one with the covered
//     line;
//   - an envelope coded one of FIXED_SENTENCE_CODES is Error(its message) (the
//     retry line when it carries no message);
//   - an envelope coded api.internal, coded anything else, or carrying no code
//     is Error(SAVE_RETRY_MESSAGE) - never the server's text;
//   - a plain-string body (openapi-fetch's error for a non-JSON response) or a
//     TypeError thrown by fetch (a network failure) is Error(SAVE_RETRY_MESSAGE);
//   - any other thrown value is returned unchanged, for the caller to rethrow.
export function toSaveError(err: unknown): unknown {
  if (typeof err === "string" || err instanceof TypeError) return new Error(SAVE_RETRY_MESSAGE);
  const envelope = asRecord(asRecord(err)?.error);
  if (envelope === undefined) return err;
  const hints = asRecord(envelope.hints) ?? {};
  const nodeId = typeof hints.node_id === "string" ? hints.node_id : "";
  const code = envelope.code;
  if (code === "draft.stale") {
    return new DraftRefusedError(code, nodeId, staleRefusalLine(hints.status, hints.reason_differs));
  }
  if (code === COVERED_BY_REFUSED_CODE) {
    return new DraftRefusedError(code, nodeId, COVERED_BY_REFUSED_LINE);
  }
  const message = envelope.message;
  if (typeof code === "string" && FIXED_SENTENCE_CODES.has(code) && typeof message === "string" && message !== "") {
    return new Error(message);
  }
  return new Error(SAVE_RETRY_MESSAGE);
}

// The part of an openapi-fetch result postDraft reads. `response` is optional
// here only so a stubbed client that returns {data, error} alone still reads as
// before; openapi-fetch itself always carries it.
type DraftPostResult<D> = { data?: D; error?: unknown; response?: { ok: boolean } };

// postDraft runs one draft POST so that every way it can fail reaches the
// caller as an Error through toSaveError: a thrown fetch failure; a non-2xx
// whose body openapi-fetch parsed into `error` (any value, a falsy one
// included); and a non-2xx with an EMPTY body, for which openapi-fetch leaves
// `error` undefined (so `response.ok` is read too). A failed response whose
// body toSaveError does not turn into an Error (a bare JSON number, an object
// that is not an error envelope) reads as the retry line. A 2xx is the success
// it always was, an `already_applied` list in its body included (0159 P2: a
// re-send of what already holds stores nothing and is not a refusal).
async function postDraft<D>(post: () => Promise<DraftPostResult<D>>): Promise<D | undefined> {
  let result: DraftPostResult<D>;
  try {
    result = await post();
  } catch (err) {
    throw toSaveError(err);
  }
  if (result.error !== undefined || result.response?.ok === false) {
    const failure = toSaveError(result.error ?? "");
    throw failure instanceof Error ? failure : new Error(SAVE_RETRY_MESSAGE);
  }
  return result.data;
}

// A refused save as the Review page's notice keeps it (0159 F2/F8): the
// server's code (a DraftRefusedError's code), the ruled line (its message), the
// refused item's id and title, and - for a covered-by row's Yes - the title of
// the decision the row played back as covering it ("" otherwise), which
// refusalNoticeLine reads to word the notice's line.
export type DraftRefusal = { code: string; line: string; nodeId: string; title: string; holderTitle: string };

// The notice's text for one refusal. A refused covered-by ruling whose question
// and holder titles are both known names both, whatever is on screen
// (coveredByHolderRefusedLine). Any other refusal reads as the ruled line while
// the refused item is the one on screen; once it is not - the founder moved on
// before the 409 arrived, or the refetch the refusal triggers dropped the item
// and the next one slid in - the line prefixed with the item's title in quotes,
// so it never reads as if the item now on screen was refused. A refusal with no
// title keeps the plain line.
export function refusalNoticeLine(refusal: DraftRefusal, onScreenNodeId: string | undefined): string {
  if (refusal.code === COVERED_BY_REFUSED_CODE && refusal.title !== "" && refusal.holderTitle !== "") {
    return coveredByHolderRefusedLine(refusal.title, refusal.holderTitle);
  }
  if (refusal.nodeId === onScreenNodeId || refusal.title === "") return refusal.line;
  return `"${refusal.title}": ${refusal.line}`;
}

// withRefusal is the notice's list after `next` lands (0159 F2/F8). The notice
// keeps EVERY refusal until the founder's navigation clears it, in the order
// they landed, newest last: a refused save stores nothing and leaves no Changes
// entry, so a second refusal landing while the first is shown (y on one
// decision, Next, y on another, both 409s arriving) must never hide the first.
// A newer refusal of the same item replaces the older one, moving to the end; a
// refusal with no id never replaces another.
export function withRefusal(refusals: readonly DraftRefusal[], next: DraftRefusal): DraftRefusal[] {
  const kept = next.nodeId === "" ? refusals : refusals.filter((r) => r.nodeId !== next.nodeId);
  return [...kept, next];
}

// The item a submit hook's panel posts for: its id and its title, and for the
// covered-by row's Yes the title of the decision the row plays back.
export type RefusalItem = { id: string; title: string; holderTitle?: string };

// refusalOf is a refusal of the hook's `item`: the refused id is the error's
// (error.hints.node_id), or the item's when the hint is absent, and the title
// and holder title are the item's only when that id is the item's, so a notice
// never names an item, or a decision, the refusal is not about.
function refusalOf(err: DraftRefusedError, item: RefusalItem | undefined): DraftRefusal {
  const nodeId = err.nodeId !== "" ? err.nodeId : (item?.id ?? "");
  const ofItem = item !== undefined && item.id === nodeId;
  return {
    code: err.code,
    line: err.message,
    nodeId,
    title: ofItem ? item.title : "",
    holderTitle: ofItem ? (item.holderTitle ?? "") : "",
  };
}

// The submit hooks' options (0159 F2/F8): `item` is the node the hook's panel
// posts for, and onRefused receives a refused save of it as a DraftRefusal -
// the Review page's notice adder. onRefused is called from the hook's
// OPTIONS-level onError, which TanStack Query runs from the mutation itself: a
// callback passed to mutate() is skipped once its observer has no listeners,
// so a founder whose panel unmounted before the 409 arrived (a queue refetch
// dropped the item, or they moved on) would never learn the choice was
// refused.
export type SubmitDraftOptions = {
  item?: RefusalItem;
  onRefused?: (refusal: DraftRefusal) => void;
};

// POST /answers + /approvals are LWW per node_id. The local cache for
// /tree + /products list (which carries denormalized queue counters)
// gets invalidated so the dashboard and product overview reflect the
// drafted state. The queue itself doesn't change shape on draft (the
// node stays in the queue, listed in drafted_node_ids; the overlay moves no
// status), so we invalidate the same queue to refetch updated state (an
// answer's helper also invalidates the decisions queue).
//
// A refused save (DraftRefusedError) invalidates the same keys - each
// hook's ONE helper, called by onSuccess and by onError's refusal arm, so the
// two lists cannot drift - and then calls onRefused with the refusal of the
// hook's item, both from the options-level onError, which runs even when the
// panel that posted has since unmounted: the refetch drops a decision that is
// no longer proposed from the decisions queue, and hides a covered-by row the
// question no longer takes.
export function useSubmitAnswer(productId: string, { item, onRefused }: SubmitDraftOptions = {}) {
  const queryClient = useQueryClient();
  // What an answer post may have moved, saved or refused.
  const invalidateAfterAnswer = () => {
    queryClient.invalidateQueries({ queryKey: ["product", productId, "queue", "questions"] });
    // A saved changed answer changes the decisions queue's served
    // trigger_blocking_node_ids and replaced_by_new_answer_node_ids (0157-15,
    // founder rulings Q5 and Q8), and re-posting the judged text changes them
    // back.
    queryClient.invalidateQueries({ queryKey: ["product", productId, "queue", "decisions"] });
    queryClient.invalidateQueries({ queryKey: ["product", productId, "tree"] });
    queryClient.invalidateQueries({ queryKey: ["products"] });
  };
  return useMutation({
    mutationFn: async (input: {
      questionId: string;
      answer: string;
      // The structured routing pick id: KEEP/REVISE (BOTH_STAND on a scope
      // question) for an AUTHORITY reconciliation (0125/0129), COVERED_BY for
      // the covered-by ruling (0148), DELEGATE (0131) or CONFIRM (0074). A
      // question card's Save & next reads it at submit from the answer itself
      // (the routing pick whose text the answer is), never from state kept
      // beside it; its one-tap Yes passes CONFIRM with the confirm option's
      // text. The free-text `answer` is an editable draft
      // the founder may have rewritten (a rationale, or for a moat KEEP the
      // edited value core), so it is NOT the routing signal — `selected_pick`
      // is. Omitted for an answer that is no routing pick's.
      selectedPick?: string;
      // 0148 A32: on a COVERED_BY pick, the id of the decision the review
      // row PLAYED BACK as already covering this answer (the question's
      // covered_by_candidate_id when the row rendered). The server refuses
      // the ruling unless it still equals the question's candidate, so a
      // holder re-pointed after the click never settles the question on a
      // decision the founder did not see. Omitted for every other answer.
      coveredByHolderId?: string;
    }) => {
      // selected_pick is a declared-optional field on the answers body
      // (orchestrator/api/openapi.yaml), decoded into models.Answer.SelectedPick
      // and routed on server-side. Set only when the caller passes a routing
      // pick id (above); omitted for an answer that carries none.
      // covered_by_holder_id is declared-optional beside it, decoded into
      // models.Answer.CoveredByHolderID; set only with a COVERED_BY pick.
      const answerItem: {
        question_id: string;
        answer: string;
        selected_pick?: string;
        covered_by_holder_id?: string;
      } = { question_id: input.questionId, answer: input.answer };
      if (input.selectedPick) answerItem.selected_pick = input.selectedPick;
      if (input.coveredByHolderId) answerItem.covered_by_holder_id = input.coveredByHolderId;
      return postDraft(() =>
        apiClient.POST("/api/v1/products/{pid}/answers", {
          params: {
            path: { pid: productId },
            header: { "X-Requested-With": "xhr" },
          },
          body: { answers: [answerItem] },
        }),
      );
    },
    onSuccess: () => invalidateAfterAnswer(),
    onError: (err) => {
      if (!(err instanceof DraftRefusedError)) return;
      invalidateAfterAnswer();
      onRefused?.(refusalOf(err, item));
    },
  });
}

export function useSubmitApproval(productId: string, { item, onRefused }: SubmitDraftOptions = {}) {
  const queryClient = useQueryClient();
  // What an approval post may have moved, saved or refused.
  const invalidateAfterApproval = () => {
    queryClient.invalidateQueries({ queryKey: ["product", productId, "queue", "decisions"] });
    queryClient.invalidateQueries({ queryKey: ["product", productId, "tree"] });
    queryClient.invalidateQueries({ queryKey: ["products"] });
  };
  return useMutation({
    mutationFn: async (input: {
      decisionId: string;
      action: "confirm" | "reject";
      rejectionReason?: string;
    }) => {
      return postDraft(() =>
        apiClient.POST("/api/v1/products/{pid}/approvals", {
          params: {
            path: { pid: productId },
            header: { "X-Requested-With": "xhr" },
          },
          body: {
            approvals: [
              {
                decision_id: input.decisionId,
                action: input.action,
                ...(input.action === "reject" && input.rejectionReason
                  ? { rejection_reason: input.rejectionReason }
                  : {}),
              },
            ],
          },
        }),
      );
    },
    onSuccess: () => invalidateAfterApproval(),
    onError: (err) => {
      if (!(err instanceof DraftRefusedError)) return;
      invalidateAfterApproval();
      onRefused?.(refusalOf(err, item));
    },
  });
}
